import { Navigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Page } from "../../../common/components/Page";
import { useQueueEntry } from "../hooks/useQueueEntry";
import { useAssignment } from "../hooks/useAssignment";
import { useSecondsUntil, formatMmSs } from "../lib/countdown";
import { isTerminalQueueEntryStatus } from "../lib/queueEntryStatus";
import { playerHandleSuffix } from "@bw/shared/components";
import { getQueueAssignmentPolicyCopy } from "@bw/shared/utils";
import type { components } from "@bw/shared/types/generated";

/**
 * Queue-ASSIGNED ("you're up") surface.
 *
 * Shown once an entry flips to status "assigned". Frictionless drop-in
 * (2026-07-02): a drop-in assignment is ON COURT immediately — there is NO
 * arrival-confirmation step, NO no-show grace countdown, and NO "I'm here —
 * confirm" tap. The screen is a positive "You're on {Court} — go play": it
 * renders the assigned court's authoritative NAME from the assignment response,
 * shows who else is on the court, and shows the ~30-min
 * ROTATION-BLOCK time-remaining as INFORMATIONAL (how long your turn runs) — never
 * a deadline the player must act on.
 *
 * No check-in QR pass is shown here — paying already checked the player in.
 *
 * Assignment data comes from the REAL GET /queue/{entry_id}/assignment (via
 * `useAssignment` → `queueApi.getAssignment`). When that 404s — the entry has no
 * assignment yet — the page degrades honestly to "Finding you a court" with NO
 * court name, never fabricating a court. Extend is intentionally omitted: it has
 * no HTTP route, so a button would be a silent no-op.
 */
export interface QueueAssignedPageProps {
  sessionExpiresAt?: string | null;
  sessionExpiryLabel?: string | null;
}

export function QueueAssignedPage({
  sessionExpiresAt = null,
  sessionExpiryLabel = null,
}: QueueAssignedPageProps = {}) {
  const { facilityId = "", entryId = "" } = useParams<{
    facilityId: string;
    entryId: string;
  }>();

  const assignmentQuery = useAssignment(entryId, true);

  const assignment = assignmentQuery.data ?? null;
  const courtName = assignment?.court_name ?? null;
  const policyCopy =
    assignment !== null
      ? getQueueAssignmentPolicyCopy({
          assignmentType: assignment.assignment_type,
          policyReason: assignment.policy_reason,
        })
      : null;

  // The parent waiting surface owns the facility court-state stream, so a
  // naturally completed block or staff End session invalidates this entry.
  const entryQuery = useQueueEntry(entryId);
  const entryStatus = entryQuery.data?.status;

  // Informational block time-remaining: ~30-min rotation timer from expires_at.
  // Called unconditionally to keep hook order stable.
  const blockSecondsLeft = useSecondsUntil(assignment?.expires_at ?? null);
  const sessionExpiryFact = sessionExpiryLabel ? (
    <Typography variant="body2" color="text.secondary">
      <time dateTime={sessionExpiresAt ?? undefined}>{sessionExpiryLabel}</time>
    </Typography>
  ) : null;

  // ---- terminal entry states (party-return-after-game fix) --------------------
  // Placed ABOVE the assignment-error branch so a terminal entry never falls
  // through to the "Finding you a court" degraded copy.
  //
  // The ~30-min block elapsed naturally (completed), a staff clear/TTL ended
  // it (expired), or a dup-join cancelled it (cancelled, #1522) — the entry
  // is dead either way. Route straight back to the session hub instead of an
  // in-page "your game ended, tap here" screen: the hub now surfaces the
  // reopened party for a one-tap re-queue (or the join CTA). The SR-only
  // alert preserves the state-change announcement the old in-page terminal
  // screen gave assistive tech even though nothing else renders here anymore.
  const isTerminal = isTerminalQueueEntryStatus(entryStatus);

  if (isTerminal) {
    return (
      <>
        <Box
          role="alert"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            p: 0,
            m: "-1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          Your spot ended — taking you back to your session.
        </Box>
        <Navigate to={`/dropin/${facilityId}/session`} replace />
      </>
    );
  }

  // ---- honest degraded state: no assignment data → no fabricated court ----
  // Only reached when entry is still "assigned" but the assignment GET failed.
  if (!assignment && (assignmentQuery.isError || assignmentQuery.isFetched)) {
    return (
      <Page>
        <Stack spacing={2}>
          <Box>
            <Typography component="h1" variant="h4">
              Finding you a court
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              You&apos;ll see your court here as soon as it&apos;s ready. Keep
              this screen open.
            </Typography>
          </Box>
          {sessionExpiryFact}
        </Stack>
      </Page>
    );
  }

  return (
    <Page>
      <Stack spacing={2}>
        <Typography component="h1" variant="h4">
          You&apos;re on {courtName ?? "your court"} — go play
        </Typography>
        {sessionExpiryFact}

        {assignmentQuery.isLoading ? (
          <Stack
            direction="row"
            spacing={1}
            role="status"
            aria-live="polite"
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={18} />
            <Typography color="text.secondary">Loading your court…</Typography>
          </Stack>
        ) : assignment ? (
          <Paper
            variant="outlined"
            role="group"
            aria-label="Court assignment"
            sx={{ p: 2 }}
          >
            <Stack spacing={2}>
              <Chip
                size="small"
                color="primary"
                label="Court"
                sx={{ alignSelf: "flex-start" }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {assignment.court_name}
              </Typography>
              {policyCopy !== null ? (
                <Typography color="text.secondary">{policyCopy}</Typography>
              ) : null}
              <CoPlayersSection coPlayers={assignment.co_players ?? []} />
              <Box aria-live="polite">
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {blockSecondsLeft != null && blockSecondsLeft > 0
                    ? formatMmSs(blockSecondsLeft)
                    : `${assignment.time_remaining_minutes ?? 0} min`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  left on this court block
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// CoPlayersSection — "in a game with N others"
//
// Renders only when co_players is non-empty (solo → no section).
// No-identity fence: unresolved names degrade to an em-dash. The opaque
// presence_id is the only key/handle source; raw user IDs are not in this DTO.
// ---------------------------------------------------------------------------

type CourtBoardPresenceDTO = components["schemas"]["CourtBoardPresence"];

function resolvedName(cp: CourtBoardPresenceDTO): string | null {
  const name = cp.display_name?.trim();
  return name && name !== cp.presence_id ? name : null;
}

function CoPlayersSection({
  coPlayers,
}: {
  coPlayers: CourtBoardPresenceDTO[];
}) {
  if (coPlayers.length === 0) return null;

  return (
    <Box role="group" aria-label="In a game with">
      <Typography variant="overline" color="text.secondary">
        You + {coPlayers.length} {coPlayers.length === 1 ? "other" : "others"}
      </Typography>
      <Stack
        component="ul"
        spacing={0.75}
        aria-label="Co-players"
        sx={{ m: 0, p: 0, listStyle: "none" }}
      >
        {coPlayers.map((cp) => {
          const name = resolvedName(cp);
          return (
            <Stack
              component="li"
              direction="row"
              spacing={0.75}
              key={cp.presence_id}
            >
              {name !== null ? (
                <>
                  <Typography component="span">{name}</Typography>
                  <Typography component="span" color="text.secondary">
                    {playerHandleSuffix(cp.presence_id)}
                  </Typography>
                </>
              ) : (
                <Typography component="span">—</Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
